import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useConfig } from "../../config/ConfigContext";
import { useHomeUi } from "../../context/HomeUiContext";
import formatPrice from "../../utils/formatPrice";
import { getSpecifications } from "./getSpecifications";
import { useToys } from "./ToysContext";

const FILTER_TAG_LABELS = {
  underwater: "🤿 Underwater",
  efoil: "⚡ E-Foil",
  surf: "🏄‍♂️ Surf",
  inflatable: "🛟 Inflatables",
  kids: "🧸 Kids",
  pool: "🏝️ Pools",
  platforms: "🧘‍♂️ Platforms",
};

function filterTagLabel(toy) {
  const key = toy.filters?.[0];
  if (!key) return "Equipment";
  if (FILTER_TAG_LABELS[key]) return FILTER_TAG_LABELS[key];
  return key
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function formatBrandLine(fields) {
  const raw = fields["Marque"];
  const m = raw == null ? "" : String(raw).trim();
  return m || "—";
}

export default function ToyDetail({
  toy,
  isOpen,
  setIsOpen,
  showAddToCart = false,
}) {
  const config = useConfig();
  const { setCart, cart, toys, reservationDate, availabilityByToyId } =
    useToys();
  const { showStockLimit, remindSelectRentalDate, registerToyModalCloser } =
    useHomeUi();
  const specifications = getSpecifications(toy);

  const media = [];
  if (toy.fields["Photos"]?.length > 0) {
    media.push(
      ...toy.fields["Photos"].map((photo) => ({
        type: "image",
        url: photo.url,
      })),
    );
  }
  if (toy.fields["Vidéo"]?.[0]?.url) {
    media.push({
      type: "video",
      url: toy.fields["Vidéo"][0].url,
    });
  }

  const maxQty =
    reservationDate && availabilityByToyId.hasOwnProperty(toy.id)
      ? availabilityByToyId[toy.id]
      : undefined;
  const isUnavailable = maxQty === 0;

  const [imgIdx, setImgIdx] = useState(0);
  const [modalQty, setModalQty] = useState(0);

  const cartItem = cart.find((item) => item.toy.id === toy.id);
  const cartQty = cartItem ? cartItem.quantity : 0;

  useEffect(() => {
    if (isOpen) {
      setImgIdx(0);
      setModalQty(cartQty);
    }
  }, [isOpen, toy.id, cartQty]);

  useLayoutEffect(() => {
    if (!isOpen) {
      document.body.removeAttribute("data-scroll-locked");
      document.body.style.top = "";
      return undefined;
    }
    const scrollY = window.scrollY;
    document.body.style.top = `-${scrollY}px`;
    document.body.setAttribute("data-scroll-locked", "1");
    return () => {
      document.body.removeAttribute("data-scroll-locked");
      document.body.style.top = "";
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  useEffect(() => {
    return registerToyModalCloser(() => setIsOpen(false));
  }, [registerToyModalCloser, setIsOpen]);

  const len = media.length;
  const safeIdx = len ? ((imgIdx % len) + len) % len : 0;
  const current = media[safeIdx];

  const updateCartQuantity = useCallback(
    (qty) => {
      const capped =
        maxQty !== undefined
          ? Math.min(Math.max(0, qty), maxQty)
          : Math.max(0, qty);
      setCart((prevCart) => {
        let updatedCart = [...prevCart];
        const ix = updatedCart.findIndex((c) => c.toy.id === toy.id);
        if (ix !== -1) {
          if (capped > 0) {
            updatedCart[ix] = { ...updatedCart[ix], quantity: capped };
          } else {
            updatedCart.splice(ix, 1);
          }
        } else if (capped > 0) {
          updatedCart.push({ toy, quantity: capped });
        }
        updatedCart.sort((a, b) => {
          const ia = toys.findIndex((t) => t.id === a.toy.id);
          const ib = toys.findIndex((t) => t.id === b.toy.id);
          return ia - ib;
        });
        return updatedCart;
      });
    },
    [maxQty, setCart, toy, toys],
  );

  const modalQtyChange = (delta) => {
    setModalQty((prev) => {
      if (delta > 0 && maxQty !== undefined && prev >= maxQty) {
        showStockLimit(
          toy.fields["Name"] || "this item",
          maxQty,
        );
        return prev;
      }
      const next = prev + delta;
      const floor = Math.max(0, next);
      if (maxQty !== undefined && floor > maxQty) {
        showStockLimit(toy.fields["Name"] || "this item", maxQty);
        return maxQty;
      }
      return floor;
    });
  };

  const modalConfirm = () => {
    if (isUnavailable) return;
    if (!reservationDate) {
      remindSelectRentalDate();
      return;
    }
    const q = modalQty === 0 ? 1 : modalQty;
    updateCartQuantity(q);
    setModalQty(q);
    setIsOpen(false);
  };

  const closeModal = () => setIsOpen(false);

  const handleBgClick = (e) => {
    if (e.target === e.currentTarget) closeModal();
  };

  if (!isOpen || !toy) return null;

  const price = toy.fields[config.itemPriceFieldTTC];
  const dateNotSelected = !reservationDate;

  return createPortal(
    <div
      className="modal-bg open"
      onClick={handleBgClick}
      onKeyDown={(e) => e.key === "Escape" && closeModal()}
      role="presentation"
    >
      <div
        className="modal-product"
        id="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-product-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="modal-close"
          onClick={closeModal}
          aria-label="Close"
        >
          ×
        </button>
        <div className="modal-body-grid">
          <div className="modal-gallery">
            <div className="modal-main">
              {current?.type === "video" ? (
                <video
                  key={current.url}
                  src={current.url}
                  controls
                  playsInline
                />
              ) : current ? (
                <img src={current.url} alt="" />
              ) : null}
              {len > 1 && (
                <>
                  <button
                    type="button"
                    className="modal-arr modal-arr-prev"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setImgIdx((i) => (i - 1 + len) % len);
                    }}
                    aria-label="Previous"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="modal-arr modal-arr-next"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setImgIdx((i) => (i + 1) % len);
                    }}
                    aria-label="Next"
                  >
                    ›
                  </button>
                  <div className="modal-counter">
                    {safeIdx + 1} / {len}
                  </div>
                </>
              )}
            </div>
            {len > 1 && (
              <div className="modal-thumbs">
                {media.map((m, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`modal-thumb${i === safeIdx ? " on" : ""}`}
                    onClick={() => setImgIdx(i)}
                  >
                    {m.type === "video" ? (
                      <span className="text-xs p-2">▶</span>
                    ) : (
                      <img src={m.url} alt="" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="modal-info">
            <div className="modal-tag">{filterTagLabel(toy)}</div>
            <h2 className="modal-name" id="modal-product-title">
              {toy.fields["Name"]}
            </h2>
            <div className="modal-price-row">
              <span className="modal-price">€{formatPrice(price)}</span>
              <span className="modal-price-unit">TTC / day</span>
            </div>
            <div className="modal-brand-row">
              <span className="modal-brand-lbl">Brand:</span>
              <span className="modal-brand">{formatBrandLine(toy.fields)}</span>
            </div>
            <div className="modal-hr" />
            {specifications.length > 0 && (
              <>
                <div className="modal-specs-lbl">Specifications</div>
                <div className="modal-specs">
                  {specifications.map((spec, index) => (
                    <div key={index} className="modal-spec">
                      <div className="modal-spec-ico">{spec.icon}</div>
                      <div className="modal-spec-lbl">{spec.name}</div>
                      <div className="modal-spec-val">
                        {spec.value != null && spec.value !== ""
                          ? String(spec.value)
                          : "—"}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
        {showAddToCart && (
          <div className="modal-foot">
            {isUnavailable ? (
              <p className="text-sm text-gray-500 italic px-2">
                Unavailable for this date
              </p>
            ) : (
              <>
                <div
                  className={`mqty${dateNotSelected ? " opacity-50" : ""}`}
                >
                  <button
                    type="button"
                    className="mqbtn"
                    onClick={() => modalQtyChange(-1)}
                    disabled={modalQty === 0 || dateNotSelected}
                  >
                    −
                  </button>
                  <span className="mqnum">{modalQty}</span>
                  <button
                    type="button"
                    className="mqbtn"
                    onClick={() => {
                      if (dateNotSelected) {
                        remindSelectRentalDate();
                        return;
                      }
                      modalQtyChange(1);
                    }}
                  >
                    +
                  </button>
                </div>
                <button
                  type="button"
                  className="modal-confirm"
                  onClick={modalConfirm}
                >
                  Add to selection →
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
